// Lib
import { NativeTypes } from 'react-dnd-html5-backend';
import { first } from 'lodash';

// Components
import ElementDropTarget from '../../element/dnd/elementDropTargets/ElementDropTarget';

// Utils
import { getCanvasLocationFromClientOffset, getGridOffset } from '../utils/canvasPositionUtils';
import * as pointsLib from '../../../common/maths/geometry/point';
import { positionPixelsToGridPoints } from '../../utils/grid/gridUtils';
import isDraggedItemATask from '../../element/dnd/task/isDraggedItemATask';
import { isCommentThread } from '../../../common/elements/utils/elementTypeUtils';
import { getWrappedReactDndMonitor } from '../../utils/dnd/GlobalDragAndDropHelper';
import { isLocationAttached } from '../../../common/elements/utils/elementLocationUtils';
import { getDefaultElementOffset } from '../../utils/dnd/dragPositionUtils';
import {
    getCanvasOriginCoordinates,
    getElementId,
    getIsCollapsed,
} from '../../../common/elements/utils/elementPropertyUtils';
import { handleCanvasFileDrop } from './canvasFileDropUtil';
import {
    dropTargetAlreadyHandled,
    handleElementDrop,
    handleImageUrlDrop,
    handleTaskDrop,
    handleTextDrop,
    handleUrlDrop,
    NATIVE_TYPES,
    CUSTOM_TYPES,
} from '../../utils/dnd/dragAndDropUtils';
import { getCommentThreadDropPosition } from '../../utils/dnd/dropPositionUtils';

// State
import dragAndDropStateSingleton, {
    getSnappedSourceClientOffsetFromDragAndDropSingleton,
} from '../../element/dnd/dragAndDropStateSingleton';

// Constants
import { DragAndDropSnapMode } from '../../workspace/dnd/dragLayerStateTypes';
import { ElementType } from '../../../common/elements/elementTypes';
import { DROP_TARGET_TYPES } from '../../../common/elements/elementConstants';

// When dropping file onto the canvas, offset the drop position by 60 pixels, just to make it feel a little nicer
const FILE_DROP_EACH_OFFSET = 30;

const getNativeDropPosition = ({ clientOffset, i, gridSize }) => ({
    x: clientOffset.x - gridSize + FILE_DROP_EACH_OFFSET * i,
    y: clientOffset.y - gridSize + FILE_DROP_EACH_OFFSET * i,
});

export const getNativeLocationOnCanvas = (props, monitor, domNode, index = 0) => {
    const { gridSize, currentBoardId, currentBoard, zoomScale, zoomTranslationPx } = props;

    const clientPositionPx = getNativeDropPosition({
        clientOffset: monitor.getClientOffset(),
        i: index,
        gridSize,
    });

    const canvasOrigin = getCanvasOriginCoordinates(currentBoard);

    const { location } = getCanvasLocationFromClientOffset(
        domNode,
        clientPositionPx,
        gridSize,
        currentBoardId,
        canvasOrigin,
        zoomScale,
        zoomTranslationPx,
    );

    return location;
};

/**
 * When dropping a comment thread onto the canvas, if it was previously attached to an element
 * but will expand when it's dropped, we need to shift the drop location to account for the grab offset.
 */
const shouldAdjustExpandedCommentThreadDropPosition = (item) => {
    if (!item) return false;

    const { element, draggedElementIds } = item;

    return (
        !!element &&
        !!draggedElementIds &&
        draggedElementIds.length === 1 &&
        isCommentThread(element) &&
        !getIsCollapsed(element) &&
        isLocationAttached(element)
    );
};

const shouldAdjustCollapsedCommentThreadDropPosition = (item) => {
    if (!item) return false;

    const { element, draggedElementIds } = item;

    return (
        !!element &&
        !!draggedElementIds &&
        draggedElementIds.length === 1 &&
        isCommentThread(element) &&
        getIsCollapsed(element)
    );
};

/**
 * Determine the drop position based on the element that was dragged.
 */
const getCanvasDropClientPosition = (monitor, gridSize, zoomScale) => {
    if (!monitor) return null;

    const item = monitor.getItem();

    if (shouldAdjustExpandedCommentThreadDropPosition(item)) {
        const offset = monitor.getClientOffset();

        const unscaledGrabOffset = getDefaultElementOffset({ element: ElementType.COMMENT_THREAD_TYPE, gridSize });
        const scaledGrabOffset = pointsLib.scale(zoomScale, unscaledGrabOffset);

        return pointsLib.translate(offset, scaledGrabOffset);
    }

    if (shouldAdjustCollapsedCommentThreadDropPosition(item)) {
        const draggedElement = first(item.draggedElements);

        return getCommentThreadDropPosition(
            draggedElement,
            monitor.getSourceClientOffset(),
            dragAndDropStateSingleton.scaledGrabOffset,
            item.scaledCustomDragOffset,
        );
    }

    if (dragAndDropStateSingleton.snapMode === DragAndDropSnapMode.NONE) {
        return monitor.getSourceClientOffset();
    }

    // If the drag is snapped, we need to determine the actual location of the drop from the drag layer state
    //  because it performs the snap calculations
    return getSnappedSourceClientOffsetFromDragAndDropSingleton() || monitor.getSourceClientOffset();
};

/**
 * Since the size of the drag preview will change based on the zoom scale of the area it is hovering, we would need to
 * recalculate the drop offset in order to drop the element in the correct position
 *
 * Documentation: https://app.milanote.com/1OV8nE1okndh8r?p=6NkiOApXzXs
 */
const getDropOffset = (scaledGrabOffset, zoomScaleOnDragSource, zoomScaleOnDropTarget) => {
    const unscaledGrabOffset = pointsLib.reverseScale(zoomScaleOnDragSource, scaledGrabOffset);
    const offsetOnHoveredTarget = pointsLib.scale(zoomScaleOnDropTarget, unscaledGrabOffset);

    return {
        x: scaledGrabOffset.x - offsetOnHoveredTarget.x,
        y: scaledGrabOffset.y - offsetOnHoveredTarget.y,
    };
};

const getElementLocationOnCanvas = (props, monitor, domNode) => {
    const {
        gridSize,
        currentBoardId,
        currentBoard,
        zoomScale,
        zoomTranslationPx,
        getContextZoomScale = () => 1,
    } = props;

    let clientPositionPx = getCanvasDropClientPosition(monitor, gridSize, zoomScale);

    const { zoomScaleOnDragSource } = monitor.getItem();
    const zoomScaleOnDropTarget = getContextZoomScale();

    // Add a drop offset on clientPositionPx. This is because the zoom scale on drop location might be different to the
    // zoom scale of the drag source.
    const dropOffset = getDropOffset(
        dragAndDropStateSingleton.scaledGrabOffset,
        zoomScaleOnDragSource,
        zoomScaleOnDropTarget,
    );
    clientPositionPx = pointsLib.translate(dropOffset, clientPositionPx);

    const canvasOrigin = getCanvasOriginCoordinates(currentBoard);

    const { location } = getCanvasLocationFromClientOffset(
        domNode,
        clientPositionPx,
        gridSize,
        currentBoardId,
        canvasOrigin,
        zoomScale,
        zoomTranslationPx,
    );

    return location;
};

const getElementCanvasDropGridOffset = (props, monitor, domNode) => {
    const { gridSize, zoomScale, zoomTranslationPx } = props;

    const dropPosition = monitor.getSourceClientOffset();

    return getGridOffset(domNode, dropPosition, gridSize, zoomScale, zoomTranslationPx);
};

const getElementCanvasDropState = (props, monitor, domNode) => ({
    dropOffset: getElementCanvasDropGridOffset(props, monitor, domNode),
});

/**
 * This is required because the drag handler usually decides what happens on drop (where the new element is
 * created).  But now we need that location for the newly created TaskList as we're creating it in the
 * drop target.
 */
const getNewlyCreatedTaskListLocation = (props, monitor, domNode) => {
    const { gridSize } = props;
    const { element, unscaledElementOffsetsMap } = monitor.getItem();

    const location = getElementLocationOnCanvas(props, monitor, domNode);

    // FIXME-DRAG-OFFSET This is a hack, similar to that found in dragAndDropUtils.grabbedElementRequiresOffset
    // The new Task does not need an offset like an existing task, because existing tasks are grabbed from the right.
    const elementId = getElementId(element);
    const unscaledElementOffset = positionPixelsToGridPoints(unscaledElementOffsetsMap[elementId], gridSize);
    const position = pointsLib.translate(location.position, unscaledElementOffset);

    return {
        ...location,
        position,
    };
};

const handleCanvasTaskDrop = handleTaskDrop(getNewlyCreatedTaskListLocation);
const handleCanvasElementDrop = handleElementDrop(getElementLocationOnCanvas, getElementCanvasDropState);
const handleCanvasTextDrop = handleTextDrop(getNativeLocationOnCanvas);
const handleCanvasUrlDrop = handleUrlDrop(getNativeLocationOnCanvas);
const handleCanvasImageUrlDrop = handleImageUrlDrop(getNativeLocationOnCanvas);

// Drop Target functionality. Object returned by this function will become the dropResult.
const dropLocationFn = (props, dndMonitor, domNode) => {
    const monitor = getWrappedReactDndMonitor(dndMonitor);

    // Don't handle the drop if it's already been handled by a child.
    if (dropTargetAlreadyHandled(monitor)) return;

    // Don't handle the drop if it's already been handled by something else on the canvas.
    if (monitor.didDrop()) return;

    if (monitor.getItemType() === CUSTOM_TYPES.IMAGE_LINK) return handleCanvasImageUrlDrop(props, monitor, domNode);
    if (monitor.getItemType() === NativeTypes.TEXT) return handleCanvasTextDrop(props, monitor, domNode);
    if (monitor.getItemType() === NativeTypes.FILE) return handleCanvasFileDrop(props, monitor, domNode);
    if (monitor.getItemType() === NativeTypes.URL) return handleCanvasUrlDrop(props, monitor, domNode);
    if (isDraggedItemATask(monitor)) return handleCanvasTaskDrop(props, monitor, domNode);

    const { draggedElementIds } = monitor.getItem();
    return handleCanvasElementDrop(props, monitor, domNode, draggedElementIds);
};

export default ElementDropTarget({
    drop: dropLocationFn,
    hoverType: DROP_TARGET_TYPES.CANVAS,
    acceptDropTypes: NATIVE_TYPES,
    collect: () => {
        // return nothing here - no reason for canvas to update during drag
        return {};
    },
});
